/**
 * The request core behind real.ajax, available as the global howspace.ajax.
 *
 * Other code uses the global, never a runtime import of this module: a bundle that imported it would get its own
 * copy with its own state and listeners. TypeScript callers may `import type` from here.
 */

declare const real: any;

declare global {
    var howspace: {
        ajax: HowspaceAjax;
        [key: string]: any;
    };
}

export type AjaxErrorKind = 'http' | 'network' | 'parse';

export type AjaxResponseType = 'auto' | 'json' | 'text';

/** false, or a function returning false for the error, turns the notification or the log off. */
export type AjaxFlag = boolean | ((error: AjaxError) => boolean | void);

export interface AjaxOptions {
    /** 'GET' by default. */
    method?: string;
    /** Appended to the URL, an object is serialized like jQuery.param(). */
    query?: Record<string, unknown> | string;
    /** A plain object or array is sent as JSON. Strings, FormData, Blob and URLSearchParams are sent as is. */
    body?: unknown;
    /** Extra request headers, they override the defaults. */
    headers?: HeadersInit;
    signal?: AbortSignal;
    /** 'auto' (the default) parses JSON when the response says it is JSON and returns text otherwise. */
    responseType?: AjaxResponseType;
    /** Whether to wait for the websocket connection. By default only requests other than GET wait. */
    websocket?: boolean;
    /** Whether failures that may be temporary are retried. By default only GET requests are. */
    retry?: boolean;
    notify?: AjaxFlag;
    log?: AjaxFlag;
    /** Sends the request with XMLHttpRequest, the only API that reports upload progress. */
    onUploadProgress?: (event: ProgressEvent) => void;
}

export interface AjaxRawResponse {
    status: number;
    statusText: string;
    headers: Headers;
    text: string;
}

export interface AjaxResponse<T = any> extends AjaxRawResponse {
    data: T;
}

export interface AjaxErrorDetails {
    key?: string;
    code?: number;
    message?: string;
    property?: string;
    [key: string]: unknown;
}

/** The XHR-like shape the error notification, the error log and the real.ajax callbacks read. */
export interface LegacyResponse {
    readyState: number;
    responseText: string;
    status: number;
    statusText: string;
    responseJSON?: any;
    getResponseHeader(name: string): string | null;
    getAllResponseHeaders(): string;
}

export interface ReportOptions {
    notify?: boolean;
    log?: boolean;
    /** The request body, attached to the error log entry. */
    body?: unknown;
    /** What the notification and the log describe, the XHR-like response by default. */
    details?: LegacyResponse | {status: number; responseJSON?: any; [key: string]: unknown};
}

export interface HowspaceAjax {
    Error: typeof AjaxError;
    request<T = any>(url: string, options?: AjaxOptions): Promise<AjaxResponse<T>>;
    get<T = any>(url: string, options?: AjaxOptions): Promise<T>;
    post<T = any>(url: string, body?: unknown, options?: AjaxOptions): Promise<T>;
    put<T = any>(url: string, body?: unknown, options?: AjaxOptions): Promise<T>;
    patch<T = any>(url: string, body?: unknown, options?: AjaxOptions): Promise<T>;
    delete<T = any>(url: string, options?: AjaxOptions): Promise<T>;
    isAbort(error: unknown): boolean;
    getErrors(response: unknown): AjaxErrorDetails[];
    param(object: unknown): string;
    report(error: AjaxError, options?: ReportOptions): void;
    toLegacyResponse(error: Pick<AjaxError, 'status' | 'statusText' | 'text' | 'headers' | 'data'>): LegacyResponse;
}

const RETRY_DELAYS_MS = [3000, 5000, 10000, 30000, 60000];
const MAX_RETRY_AFTER_MS = 60 * 1000;
const MAX_OFFLINE_WAIT_MS = 60 * 1000;
const RETRYABLE_STATUSES = [0, 408, 429, 502, 503, 504];
// Validation and infrastructure failures are expected, so they are not logged as errors.
const UNLOGGED_STATUSES = [422, 502, 503, 504];
// After a response reports a new Howspace version, the websocket update notification gets this long to arrive
// before the next response reloads the page. Old and new versions run side by side during a deploy.
const VERSION_RELOAD_THRESHOLD_MS = 5 * 60 * 1000;
const ERROR_ICON = '<i class="fa-light fa-ban mr-2" aria-hidden="true"></i>';

// Errors already shown and logged here, so leaving them uncaught does not add console noise.
const handledErrors = new WeakSet<object>();
let versionChangeSeenAt: number | null = null;
let websocketReady = false;
real.realtime.onReady(() => {
    websocketReady = true;
});

window.addEventListener('unhandledrejection', event => {
    if (handledErrors.has(event.reason)) {
        event.preventDefault();
    }
});

const parseJson = (text: string): any => {
    try {
        return JSON.parse(text);
    } catch (e) {
        return undefined;
    }
};

const getErrors = (response: any): AjaxErrorDetails[] => {
    const errors = response?.errors || response?.responseJSON?.errors || null;
    if (typeof errors !== 'object' || errors === null) {
        return [];
    }
    return Object.values(errors).map((obj: any) => obj?.error);
};

export class AjaxError extends Error {
    method: string;
    url: string;
    status: number;
    statusText: string;
    headers: Headers;
    text: string;
    kind: AjaxErrorKind;
    /** The parsed response body, when it is JSON. */
    data: any;
    /** True when the page is navigating away because of this error, such as an expired session. */
    redirecting = false;
    callStack: string | null = null;

    constructor(message: string, {method, url, status = 0, statusText = '', headers = new Headers(), text = '', kind}: {
        method: string;
        url: string;
        status?: number;
        statusText?: string;
        headers?: Headers;
        text?: string;
        kind: AjaxErrorKind;
    }) {
        super(message);
        this.name = 'AjaxError';
        this.method = method;
        this.url = url;
        this.status = status;
        this.statusText = statusText;
        this.headers = headers;
        this.text = text;
        this.kind = kind;
        this.data = parseJson(text);
    }

    get errors(): AjaxErrorDetails[] {
        return getErrors(this.data);
    }

    hasKey(key: string): boolean {
        return this.errors.some(error => error?.key === key);
    }
}

const isAbort = (error: unknown): boolean => (error as Error)?.name === 'AbortError';

const createAbortError = (signal?: AbortSignal): Error => {
    const error = signal?.reason?.name === 'AbortError'
        ? signal.reason
        : new DOMException('The request was aborted.', 'AbortError');
    handledErrors.add(error);
    return error;
};

const toType = (value: unknown): string => {
    if (value === null || value === undefined) {
        return String(value);
    }
    if (typeof value !== 'object' && typeof value !== 'function') {
        return typeof value;
    }
    const tag = Object.prototype.toString.call(value).slice(8, -1);
    return ['Boolean', 'Number', 'String', 'Function', 'Array', 'Date', 'RegExp', 'Error', 'Symbol'].includes(tag)
        ? tag.toLowerCase()
        : 'object';
};

/**
 * Serializes an object the way jQuery.param() does, with nested objects and arrays in PHP bracket notation.
 */
const param = (object: unknown): string => {
    const parts: string[] = [];
    const add = (key: string, valueOrFunction: unknown) => {
        const value = typeof valueOrFunction === 'function' ? valueOrFunction() : valueOrFunction;
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value == null ? '' : value as any));
    };
    const build = (prefix: string, value: any) => {
        if (Array.isArray(value)) {
            value.forEach((item, i) => {
                if (/\[\]$/.test(prefix)) {
                    add(prefix, item);
                } else {
                    build(prefix + '[' + (typeof item === 'object' && item != null ? i : '') + ']', item);
                }
            });
        } else if (toType(value) === 'object') {
            for (const name in value) {
                build(prefix + '[' + name + ']', value[name]);
            }
        } else {
            add(prefix, value);
        }
    };

    if (object == null) {
        return '';
    }
    for (const prefix in object as object) {
        build(prefix, (object as any)[prefix]);
    }
    return parts.join('&');
};

const appendQuery = (url: string, query: AjaxOptions['query']): string => {
    const queryString = typeof query === 'string' ? query : param(query);
    if (!queryString) {
        return url;
    }
    const hashIndex = url.indexOf('#');
    const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    return base + (base.includes('?') ? '&' : '?') + queryString + hash;
};

const isSameOrigin = (url: string): boolean => {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
        return false;
    }
};

const isPlainObject = (value: unknown): boolean => {
    if (toType(value) !== 'object') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === null || proto === Object.prototype;
};

const buildHeaders = (url: string, options: AjaxOptions): Headers => {
    const headers = new Headers();
    if (isSameOrigin(url)) {
        headers.set('X-Requested-With', 'XMLHttpRequest');
    }
    const windowTopicId = document.documentElement.dataset.real4WindowPubsubTopicId;
    if (windowTopicId !== undefined) {
        headers.set('X-Real4-Window-Pubsub-Topic-Id', windowTopicId);
    }
    headers.set('X-Howspace-WebsocketId', real.realtime?.uwebsocket?.getConnectionId() || '');
    if (isPlainObject(options.body) || Array.isArray(options.body)) {
        headers.set('Content-Type', 'application/json');
    }
    headers.set('Accept', 'application/json, text/plain, */*');
    new Headers(options.headers || {}).forEach((value, name) => headers.set(name, value));
    return headers;
};

const serializeBody = (body: unknown): BodyInit | null => {
    if (isPlainObject(body) || Array.isArray(body)) {
        return JSON.stringify(body);
    }
    return (body ?? null) as BodyInit | null;
};

// A write may start work whose result only arrives over the websocket, so it waits until the page has
// subscribed. Reads do not, unless they opt in to send a connection id in X-Howspace-WebsocketId.
const shouldWaitForWebsocket = (method: string, option: boolean | undefined): boolean => {
    if (typeof option === 'boolean') {
        return option;
    }
    return method !== 'GET' && method !== 'HEAD' && document.documentElement.hasAttribute('data-uwebsocket-uri');
};

const waitForWebsocket = (signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    signal?.addEventListener('abort', () => reject(createAbortError(signal)), {once: true});
    real.realtime.onReady(resolve);
});

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(createAbortError(signal));
    }, {once: true});
});

const waitForOnline = (maxMs: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    const done = () => {
        clearTimeout(timeoutId);
        window.removeEventListener('online', done);
        resolve();
    };
    const timeoutId = setTimeout(done, maxMs);
    window.addEventListener('online', done);
    signal?.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        window.removeEventListener('online', done);
        reject(createAbortError(signal));
    }, {once: true});
});

const parseRawHeaders = (raw: string): Headers => {
    const headers = new Headers();
    raw.trim().split(/[\r\n]+/).forEach(line => {
        const index = line.indexOf(':');
        if (index > 0) {
            headers.append(line.slice(0, index).trim(), line.slice(index + 1).trim());
        }
    });
    return headers;
};

interface SendInit {
    method: string;
    headers: Headers;
    body: BodyInit | null;
    signal?: AbortSignal;
    onUploadProgress?: (event: ProgressEvent) => void;
}

const sendWithFetch = async (url: string, init: SendInit): Promise<AjaxRawResponse> => {
    const response = await fetch(url, {
        method: init.method,
        headers: init.headers,
        body: init.body,
        signal: init.signal,
        credentials: 'same-origin',
    });
    return {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        text: await response.text(),
    };
};

const sendWithXhr = (url: string, init: SendInit) => new Promise<AjaxRawResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method, url);
    init.headers.forEach((value, name) => xhr.setRequestHeader(name, value));
    xhr.upload.addEventListener('progress', init.onUploadProgress);
    xhr.addEventListener('load', () => resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseRawHeaders(xhr.getAllResponseHeaders()),
        text: xhr.responseText,
    }));
    xhr.addEventListener('error', () => reject(new TypeError('Network request failed.')));
    xhr.addEventListener('abort', () => reject(createAbortError(init.signal)));
    init.signal?.addEventListener('abort', () => xhr.abort(), {once: true});
    xhr.send(init.body as XMLHttpRequestBodyInit | null);
});

const parseRetryAfter = (value: string | null): number | null => {
    if (!value) {
        return null;
    }
    const seconds = Number(value);
    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000);
    }
    const date = Date.parse(value);
    return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

/**
 * Milliseconds to wait before trying again, or null when the request must not be retried.
 */
const getRetryDelay = (retrySafe: boolean, response: AjaxRawResponse, attempt: number) => {
    if (!retrySafe || attempt >= RETRY_DELAYS_MS.length || !RETRYABLE_STATUSES.includes(response.status)) {
        return null;
    }
    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
    if (retryAfter !== null) {
        return retryAfter > MAX_RETRY_AFTER_MS ? null : retryAfter;
    }
    // Without Retry-After a 429 is the application's own rate limit, which a retry only hits again.
    return response.status === 429 ? null : RETRY_DELAYS_MS[attempt];
};

const checkVersionAndIdle = (headers: Headers) => {
    const version = headers.get('X-Howspace-Version') || null;
    if (version !== null && version !== document.documentElement.dataset.howspaceVersion) {
        const now = performance.now();
        if (versionChangeSeenAt === null) {
            versionChangeSeenAt = now;
            console.log('HS: Noticed version change', version);
        } else if (now - versionChangeSeenAt > VERSION_RELOAD_THRESHOLD_MS) {
            console.log('HS: Triggering version change', version);
            real.window.reloadPageOrNotifyUser(howspace.translate('layout_version_updated'));
            versionChangeSeenAt = null;
        }
    }

    if (howspace.activityTracker?.isIdle() && document.hasFocus()) {
        // The user was disconnected for being idle and is now active in this tab again.
        setTimeout(() => {
            real.window.reloadPageOrNotifyUser(howspace.translate('layout_disconnected_due_to_inactivity'), false);
        }, 1000);
    }
};

/**
 * Logs the request stats that non-production servers send in the X-Howspace-* and Server-Timing headers.
 */
const logRequestStats = (headers: Headers, method: string, url: string) => {
    const queries = headers.get('X-Howspace-Queries');
    if (queries === null) {
        return;
    }
    const totalMs = /(?:^|,)\s*total;dur=([\d.]+)/.exec(headers.get('Server-Timing') || '')?.[1];

    let relativeUrl = url;
    if (/^https?:\/\//.test(url)) {
        const urlObject = new URL(url);
        relativeUrl = urlObject.pathname + urlObject.search;
    }
    real.logRequestStats(
        method,
        relativeUrl,
        Number(totalMs),
        Number(queries),
        Number(headers.get('X-Howspace-Memory-Peak-Mb')),
        headers.get('X-Howspace-Request-Log')
    );
};

const readResponseData = (response: AjaxRawResponse, responseType: AjaxResponseType) => {
    if (response.status === 204 || responseType === 'text') {
        return response.status === 204 ? undefined : response.text;
    }
    const isJson = responseType === 'json' || /\bjson\b/.test(response.headers.get('Content-Type') || '');
    if (!isJson) {
        return response.text;
    }
    if (response.text === '') {
        return undefined;
    }
    return JSON.parse(response.text);
};

const toLegacyResponse: HowspaceAjax['toLegacyResponse'] = error => {
    const response = {
        readyState: error.status > 0 ? 4 : 0,
        responseText: error.text,
        status: error.status,
        statusText: error.statusText || 'error',
    } as LegacyResponse;
    if (error.data !== undefined) {
        response.responseJSON = error.data;
    }
    Object.defineProperties(response, {
        getResponseHeader: {value: (name: string) => error.headers.get(name)},
        getAllResponseHeaders: {
            value: () => [...error.headers].map(([name, value]) => name + ': ' + value).join('\r\n'),
        },
    });
    return response;
};

const capitalizedHeaders = (headers: Headers): Record<string, string> => {
    const result: Record<string, string> = {};
    headers.forEach((value, name) => {
        result[name.split('-').map(word => word.slice(0, 1).toUpperCase() + word.slice(1)).join('-')] = value;
    });
    return result;
};

const showErrorNotification = (url: string, details: ReportOptions['details']) => {
    const errors = details?.responseJSON?.errors;
    if (errors?.length > 0 && errors[0].error) {
        real.notification.show(ERROR_ICON + errors[0].error.message, 'danger');
        return;
    }

    let content = ERROR_ICON + 'AJAX request to URI "' + url + '" failed with error code ' + details.status + '.';
    const adminUri = document.documentElement.dataset.adminUri;
    if (adminUri) {
        content += ' <a href="' + adminUri + '/manager/errors" ' +
            'target="_blank" rel="noopener noreferrer nofollow" class="no-check-href">View error</a>';
    }
    real.notification.show(content, 'danger');
};

const logError = (error: AjaxError, details: ReportOptions['details'], body: unknown) => {
    const message = 'AJAX ' + error.method + ' ' + error.url + ' failed: ' + JSON.stringify(details);
    const logged: Error & {responseHeaders?: Record<string, string>; dataPayload?: unknown} = new Error(message);
    if (error.callStack) {
        logged.stack = real.errorlogger.cleanErrorStack(error.callStack);
    }
    logged.responseHeaders = capitalizedHeaders(error.headers);
    if (body) {
        logged.dataPayload = body;
    }
    real.errorlogger.sendErrorToServer(message, real.cdnUri + '/assets/js/real/ajax.js', 1, 1, logged);
};

/**
 * Shows the error notification and sends the error to the error log, unless the page is being left.
 */
const report = (error: AjaxError, {notify = true, log = true, body = null, details}: ReportOptions = {}) => {
    details ??= toLegacyResponse(error);
    handledErrors.add(error);
    if (real.window.isUnloading()) {
        return;
    }
    if (notify) {
        showErrorNotification(error.url, details);
    }
    if (log && !UNLOGGED_STATUSES.includes(details.status)) {
        logError(error, details, body);
    }
};

const resolveFlag = (flag: AjaxFlag | undefined, error: AjaxError): boolean => {
    return typeof flag === 'function' ? flag(error) !== false : flag !== false;
};

const handleSessionErrors = (error: AjaxError) => {
    if (error.status !== 403) {
        return;
    }
    if (error.hasKey('session_required')) {
        error.redirecting = true;
        setTimeout(() => {
            window.location.href = real.baseUri + '/?session_expired=1';
        }, 1000);
    } else if (error.hasKey('ip_not_allowed')) {
        error.redirecting = true;
        window.location.href = real.baseUri + '/error/ipnotallowed';
    }
};

/**
 * Rejects with an AjaxError, or with an AbortError when options.signal aborts the request.
 */
const request = async <T = any>(url: string, options: AjaxOptions = {}): Promise<AjaxResponse<T>> => {
    const method = (options.method || 'GET').toUpperCase();
    const callStack = new Error().stack;
    const fullUrl = appendQuery(url, options.query);
    const signal = options.signal;

    if ((method === 'GET' || method === 'HEAD') && options.body != null) {
        throw new TypeError(`A ${method} request cannot have a body.`);
    }
    if (signal?.aborted) {
        throw createAbortError(signal);
    }
    // Skipping the await once the websocket is ready sends requests in the order they were made.
    if (!websocketReady && shouldWaitForWebsocket(method, options.websocket)) {
        await waitForWebsocket(signal);
    }

    const fail = (message: string, response: AjaxRawResponse, kind: AjaxErrorKind): AjaxError => {
        const error = new AjaxError(message, {method, url, ...response, kind});
        error.callStack = callStack;
        handleSessionErrors(error);
        if (error.redirecting) {
            handledErrors.add(error);
        } else {
            const notify = resolveFlag(options.notify, error);
            report(error, {notify, log: resolveFlag(options.log, error), body: options.body});
        }
        return error;
    };

    // A request that is not safe to repeat is never retried once sent, because a lost response does not tell
    // whether the server handled it.
    const retrySafe = options.retry ?? method === 'GET';
    const send = typeof options.onUploadProgress === 'function' ? sendWithXhr : sendWithFetch;
    const init: SendInit = {
        method,
        headers: buildHeaders(fullUrl, options),
        body: serializeBody(options.body),
        signal,
        onUploadProgress: options.onUploadProgress,
    };

    for (let attempt = 0; ; attempt++) {
        // Holding it back while offline is safe, it cannot have reached the server yet.
        if (!retrySafe && navigator.onLine === false) {
            await waitForOnline(MAX_OFFLINE_WAIT_MS, signal);
        }

        let response: AjaxRawResponse;
        try {
            response = await send(fullUrl, init);
        } catch (e) {
            if (signal?.aborted || isAbort(e)) {
                throw createAbortError(signal);
            }
            response = {status: 0, statusText: '', headers: new Headers(), text: ''};
        }

        // After the caller's callbacks, so a reload notification comes last.
        setTimeout(() => checkVersionAndIdle(response.headers));

        const isSuccess = response.status >= 200 && response.status < 300 || response.status === 304;
        if (isSuccess) {
            logRequestStats(response.headers, method, url);
            try {
                return {...response, data: readResponseData(response, options.responseType)};
            } catch (e) {
                throw fail('Response is not valid JSON.', response, 'parse');
            }
        }

        const delay = getRetryDelay(retrySafe, response, attempt);
        if (delay === null) {
            const kind = response.status === 0 ? 'network' : 'http';
            throw fail(`${method} ${url} failed with status ${response.status}.`, response, kind);
        }

        if (response.status === 0 && navigator.onLine === false) {
            await waitForOnline(delay, signal);
        } else {
            await sleep(delay, signal);
        }
    }
};

const requestData = <T>(url: string, options: AjaxOptions) => request<T>(url, options).then(response => response.data);

howspace.ajax = {
    Error: AjaxError,
    request,
    get: (url, options = {}) => requestData(url, {...options, method: 'GET'}),
    post: (url, body, options = {}) => requestData(url, {...options, method: 'POST', body}),
    put: (url, body, options = {}) => requestData(url, {...options, method: 'PUT', body}),
    patch: (url, body, options = {}) => requestData(url, {...options, method: 'PATCH', body}),
    delete: (url, options = {}) => requestData(url, {...options, method: 'DELETE'}),
    isAbort,
    getErrors,
    param,
    report,
    toLegacyResponse,
};
